import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";

/**
 * US-115 — form draft save & resume.
 *
 * A draft is an in-progress fill of a schema-driven form, persisted so a
 * different authorized staff member (e.g. a supervisor co-signing) can resume
 * and finalise it later. Drafts are facility-scoped on the backend.
 *
 * US-115 follow-up: staff-audience drafts also carry `employee_id`, scoping
 * them to the filer's own employee row (server-derived, never client-set) so
 * two employees drafting the same staff-audience template at one facility
 * don't collide. Resident- and facility-audience drafts keep `employee_id`
 * null and stay facility-shared (co-sign friendly), as before.
 */
export interface FormDraft {
  id: string;
  form_template_id: string;
  template_version: number;
  facility_id: string;
  resident_id: string | null;
  requirement_id: string | null;
  employee_id: string | null;
  /**
   * The partial fill. Omitted by the list endpoint (which strips it to avoid
   * shipping signature blobs) — only present when fetched via useFormDraft(id).
   */
  data?: Record<string, unknown>;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined display fields (list endpoint only).
  form_template_name?: string | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
}

interface FormDraftFilters {
  residentId?: string;
  employeeId?: string;
  facilityId?: string;
}

/**
 * List open drafts (facility-scoped server-side; no `data`).
 *
 * - Pass `residentId` for a resident's ad-hoc/required-form drafts.
 * - Pass `employeeId` for "my" staff-audience form drafts.
 * - Pass `facilityId` alone for facility-audience drafts (resident_id AND
 *   employee_id both null) — filter the result client-side for that shape,
 *   since the backend only narrows by whichever params are present.
 *
 * Requires at least one scoping param, or the query stays disabled — this
 * hook is never meant to fetch every draft at every accessible facility.
 */
export function useFormDrafts(filters: FormDraftFilters, enabled = true) {
  const { residentId, employeeId, facilityId } = filters;
  return useQuery<FormDraft[]>({
    queryKey: [
      "form-drafts",
      residentId ?? null,
      employeeId ?? null,
      facilityId ?? null,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (residentId) params.append("resident_id", residentId);
      if (employeeId) params.append("employee_id", employeeId);
      if (facilityId) params.append("facility_id", facilityId);
      const { data } = await api.get(
        `/form-submissions/drafts?${params.toString()}`,
      );
      return data.data;
    },
    enabled: enabled && !!(residentId || employeeId || facilityId),
  });
}

/** Fetch a single draft WITH its `data` payload — used to resume. */
export function useFormDraft(draftId: string | undefined) {
  return useQuery<FormDraft>({
    queryKey: ["form-drafts", "detail", draftId ?? null],
    queryFn: async () => {
      const { data } = await api.get(`/form-submissions/drafts/${draftId}`);
      return data.data;
    },
    enabled: !!draftId,
  });
}

/** Save (create-or-update) a draft. No field validation runs server-side. */
export function useSaveFormDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    // Same inline-error contract as the submit hook — surfaces stay quiet.
    meta: FORM_MUTATION_META,
    mutationFn: async (draft: {
      form_template_id: string;
      facility_id: string;
      data: Record<string, unknown>;
      resident_id?: string;
      requirement_id?: string;
    }) => {
      const { data } = await api.post("/form-submissions/drafts", draft);
      return data.data as FormDraft;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-drafts"] });
    },
  });
}

/** Discard a draft. Surfaces a toast on failure (no inline surface to catch it). */
export function useDeleteFormDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draftId: string) => {
      const { data } = await api.delete(`/form-submissions/drafts/${draftId}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-drafts"] });
    },
    onError: () => {
      notifications.show({
        title: "Couldn't discard draft",
        message: "Something went wrong. Please try again.",
        color: "red",
      });
    },
  });
}
